import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import nexisLogo from "@/assets/nexis-logo-new.png";
import { useCredits } from "@/hooks/useCredits";
import { CreditCostBadge } from "@/components/CreditGate";
import { NexisBreadcrumb } from "@/components/nexis/NexisBreadcrumb";

interface Project {
  id: string;
  title: string;
  script: string | null;
  platform: string | null;
  prompt_type: string | null;
  visual_style: string | null;
  visual_style_category: string | null;
}

export default function NexisSceneCount() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { consume } = useCredits();
  
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  const [sceneCount, setSceneCount] = useState(25);
  const [recommendedCount, setRecommendedCount] = useState(25);

  useEffect(() => {
    if (projectId) {
      fetchProject();
    }
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const { data, error } = await supabase
        .from("nexis_projects")
        .select("id, title, script, platform, prompt_type, visual_style, visual_style_category")
        .eq("id", projectId)
        .single();

      if (error) throw error;
      
      setProject(data);
      
      // Calculate recommended scene count based on script length
      if (data.script) {
        const wordCount = data.script.split(/\s+/).length;
        const recommended = Math.min(40, Math.max(15, Math.round(wordCount / 15)));
        setRecommendedCount(recommended);
        setSceneCount(recommended);
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Failed to load project");
      navigate("/app/nexis-ai");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!project) return;
    
    setGenerating(true);
    try {
      const credited = await consume("generate-image-prompts", "Generate image prompts");
      if (!credited) { setGenerating(false); return; }
      // Get model recommendation
      const { data: modelData, error: modelError } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "recommend-model",
          visualStyle: project.visual_style,
          styleCategory: project.visual_style_category,
          promptType: project.prompt_type,
        },
      });

      if (modelError) throw modelError;
      
      // Generate image prompts
      const { data: promptsData, error: promptsError } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "generate-image-prompts",
          script: project.script,
          sceneCount,
          platform: project.platform,
          visualStyle: project.visual_style,
          styleCategory: project.visual_style_category,
        },
      });

      if (promptsError) throw promptsError;
      if (promptsData.error) throw new Error(promptsData.error);
      
      // Save scenes to database
      const scenes = promptsData.result.map((scene: any) => ({
        project_id: projectId,
        scene_number: scene.sceneNumber,
        duration: scene.duration,
        description: scene.description,
        image_prompt: scene.prompt,
      }));

      // Delete existing scenes first
      await supabase
        .from("nexis_scenes")
        .delete()
        .eq("project_id", projectId);

      const { error: insertError } = await supabase
        .from("nexis_scenes")
        .insert(scenes);

      if (insertError) throw insertError;

      // Update project with scene count and model recommendations
      const { error: updateError } = await supabase
        .from("nexis_projects")
        .update({
          scene_count: sceneCount,
          recommended_model: modelData.result.recommendedModel,
          secondary_model: modelData.result.secondaryModel,
          last_step: "image-prompts",
        })
        .eq("id", projectId);

      if (updateError) throw updateError;

      toast.success("Scene prompts generated!");
      navigate(`/app/nexis-ai/project/${projectId}/image-prompts`);
    } catch (error: any) {
      console.error("Error generating prompts:", error);
      toast.error(error.message || "Failed to generate prompts");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 min-h-screen">
      <div className="max-w-2xl mx-auto">
        <NexisBreadcrumb projectTitle={project?.title} currentStep="Scene Count" />
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <img src={nexisLogo} alt="Nexis AI" className="w-10 h-10 object-contain" />
          <div>
            <h1 className="font-display text-2xl font-bold">Scene Count</h1>
            <p className="text-sm text-muted-foreground">{project?.title}</p>
          </div>
        </div>

        <Card className="p-8 bg-card/50 border-border/50 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
            <Sparkles className="w-4 h-4" />
            <span className="font-medium">We analyzed your script</span>
          </div>

          <h2 className="text-4xl font-bold mb-2">{sceneCount} Scenes</h2>
          <p className="text-muted-foreground mb-8">
            Recommended: {recommendedCount} scenes based on your script length
          </p>

          <div className="px-4 mb-8">
            <Slider
              value={[sceneCount]}
              onValueChange={([v]) => setSceneCount(v)}
              min={15}
              max={40}
              step={1}
              className="mb-2"
            />
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>15</span>
              <span>40</span>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <Badge variant="outline" className="text-sm py-1.5 px-3">
              Platform: {project?.platform}
            </Badge>
            <Badge variant="outline" className="text-sm py-1.5 px-3">
              Type: {project?.prompt_type === "image" ? "Image Prompts" : "Video Prompts"}
            </Badge>
            <Badge variant="outline" className="text-sm py-1.5 px-3">
              Style: {project?.visual_style}
            </Badge>
          </div>

          <Button
            variant="hero"
            size="lg"
            className="w-full gap-2"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating {sceneCount} Scene Prompts...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate {sceneCount} Scene Prompts
              </>
            )}
          </Button>
        </Card>
      </div>
    </div>
  );
}
